// A listino's name and start date (TD-083), as the server picks and checks them (listino_service.create and
// update): the New listino modal opens on the server's defaults, and both listino modals refuse what the server
// would. A start may be any day, past included, so the prices the website showed before TouchPoint can be recorded.
// Pure so it can be tested.
import type { Listino } from "../types";

/** listino_service.MAX_NAME_LENGTH. */
export const LISTINO_NAME_MAX = 80;

/** "2026-11-01" from "2026-10-11": the server's first_of_next_month. */
const firstOfNextMonth = (iso: string): string => {
  const year = Number(iso.slice(0, 4));
  const month = Number(iso.slice(5, 7));
  return month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, "0")}-01`;
};

/** A listino name as the server compares them (listino_service._name_taken): trimmed, in any case. */
const nameKey = (name: string): string => name.trim().toLowerCase();

/** The server's defaults for a new listino: "Listino vN", the next number no listino holds in any case, starting the 1st after every other start. */
export function newListinoDefaults(listini: Pick<Listino, "name" | "startDate">[], today: string): { name: string; startDate: string } {
  const names = new Set(listini.map((l) => nameKey(l.name)));
  let number = listini.length + 1;
  while (names.has(nameKey(`Listino v${number}`))) number += 1;
  const latest = listini.reduce((max, l) => (l.startDate > max ? l.startDate : max), today);
  return { name: `Listino v${number}`, startDate: firstOfNextMonth(latest) };
}

export interface ListinoCheck {
  nameError: string | undefined;
  startError: string | undefined;
  blocked: boolean;
}

/**
 * A name and a start the server takes: a name, within the limit, that no other listino has in any case (TD-083), and
 * a start on any day that no other listino has.
 */
export function listinoCheck(form: { name: string; startDate: string }, listini: Pick<Listino, "id" | "name" | "startDate">[], selfId: string | null): ListinoCheck {
  const name = form.name.trim();
  const taken = listini.some((l) => l.id !== selfId && nameKey(l.name) === nameKey(name));
  const nameError = !name
    ? "Required"
    : name.length > LISTINO_NAME_MAX
      ? `${LISTINO_NAME_MAX} characters at most`
      : taken
        ? "A listino with that name exists."
        : undefined;
  const clash = listini.some((l) => l.id !== selfId && l.startDate === form.startDate);
  const startError = !form.startDate ? "Pick a start date" : clash ? "Another listino starts that day" : undefined;
  return { nameError, startError, blocked: !!nameError || !!startError };
}
